import { useQuery } from '@tanstack/react-query'
import { Flex, Heading, Spinner, Text } from '@radix-ui/themes'
import { DATA_COVERAGE_MIN_EXPECTED_HOURS, DATA_COVERAGE_SETTLE_HOURS } from '@willie/schemas'
import { PlannedItems, SectionCard } from '~/components/SectionCard'
import { useTRPC } from '~/utils/trpc'
import { DataCoverageTable } from '../data-coverage/DataCoverageTable'
import { COVERAGE_STALE_MS } from '../data-coverage/query'
import type { StationSectionProps } from '../types'

const PLANNED_ITEMS = [
  'Data quality — frozen readings, index and consumption drift, implausible deltas',
  'Device state — provider battery, signal and firmware',
  'Index alignment — whether index readings still track the physical meter, and when they last did'
] as const

const DataCoverageSection = ({ station }: StationSectionProps) => {
  const trpc = useTRPC()
  const { data, isLoading, error } = useQuery(
    trpc.ops.stations.dataCoverage.queryOptions({ stationId: station.id }, { staleTime: COVERAGE_STALE_MS })
  )

  return (
    <Flex direction="column" gap="2">
      <Flex direction="column" gap="1">
        <Heading as="h3" size="2">
          Data coverage
        </Heading>
        <Text size="1" color="gray">
          Share of UTC hours in which a source delivered at least one non-null value, in windows ending{' '}
          {DATA_COVERAGE_SETTLE_HOURS} h ago. A ratio needs {DATA_COVERAGE_MIN_EXPECTED_HOURS} expected hours.
        </Text>
      </Flex>
      {isLoading ? (
        <Flex align="center" gap="2">
          <Spinner size="2" />
          <Text size="2" color="gray">
            Loading coverage…
          </Text>
        </Flex>
      ) : error ? (
        <Text size="2" color="red">
          Coverage unavailable · {error.message}
        </Text>
      ) : station.data_sources.length === 0 ? (
        <Text size="2" color="gray">
          No data sources attached — nothing to score.
        </Text>
      ) : data ? (
        <DataCoverageTable coverage={data} sources={station.data_sources} />
      ) : null}
    </Flex>
  )
}

export const StationHealthCard = ({ station }: StationSectionProps) => (
  <SectionCard title="Health">
    <Flex direction="column" gap="4">
      <DataCoverageSection station={station} />
      <PlannedItems intro="This section will also show:" items={PLANNED_ITEMS} />
    </Flex>
  </SectionCard>
)
